import { type ReactElement, useId, useMemo } from "react";
import type { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import { OPEN_ROLE_FIELDS, type WorkRoleField } from "@api/workItem.api";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerTriggerElement } from "@design-system/Picker/Picker.types";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    WORK_ROLES,
    getWorkMemberName,
    useEditWorkItem,
    useWorkEditability,
    useWorkTeamCandidates,
} from "./workItemEditing";
import styles from "./WorkRolePicker.module.scss";

type WorkTeamRow = Pick<
    ClientsWorkOverviewWorkRow,
    | "closeId"
    | "workspaceId"
    | "isFinished"
    | "cancelledAt"
    | "openRoles"
    | WorkRoleField
>;

interface WorkRolePickerProps {
    row: WorkTeamRow;
    field: WorkRoleField;
    /** Filled with the person holding the role, "Mixed" when several do. */
    trigger: PickerTriggerElement;
}

/** The single person on a role's open tasks, if there is one. */
export const getWorkRoleHolderId = (row: WorkTeamRow, field: WorkRoleField) => {
    const { assigneeIds } = row.openRoles[OPEN_ROLE_FIELDS[field]];
    return assigneeIds.length === 1 ? assigneeIds[0] : null;
};

/**
 * Picks who holds one role on a work item, from the people with access to
 * its client. The value comes from the role's open tasks: one assignee, or
 * "Mixed" when they disagree. A holder who lost access to the client still
 * shows by name.
 */
export const WorkRolePicker = ({
    row,
    field,
    trigger,
}: Readonly<WorkRolePickerProps>) => {
    const candidates = useWorkTeamCandidates(row.workspaceId);
    const editWorkItem = useEditWorkItem();
    const holderId = getWorkRoleHolderId(row, field);
    const isMixed =
        row.openRoles[OPEN_ROLE_FIELDS[field]].assigneeIds.length > 1;
    const members = useMemo(() => {
        const formerHolder = candidates.some((m) => m.userId === holderId)
            ? undefined
            : [...row.preparers, ...row.reviewers, ...row.managers].find(
                  (member) => member.userId === holderId,
              );
        return formerHolder ? [formerHolder, ...candidates] : candidates;
    }, [candidates, holderId, row.preparers, row.reviewers, row.managers]);
    const items = useMemo(
        () =>
            members.map((member) => {
                const name = getWorkMemberName(member);
                return {
                    value: member.userId,
                    label: name,
                    leading: (
                        <UserAvatar
                            user={{ ...member, name }}
                            size="xs"
                            aria-hidden
                        />
                    ),
                };
            }),
        [members],
    );
    const isHolderShown = members.some((m) => m.userId === holderId);

    return (
        <Picker.Select
            items={items}
            value={isHolderShown ? holderId : null}
            placeholder={isMixed ? "Mixed" : "Assign"}
            searchable
            chevron="none"
            onValueChange={(userId) => {
                const member = members.find((m) => m.userId === userId);
                if (member && userId !== holderId) {
                    editWorkItem(row, { field, member });
                }
            }}
            trigger={trigger}
        />
    );
};

/** Who held a role, by name; "-" when nobody did. */
export const WorkRoleNames = ({
    members,
}: Readonly<{ members: CloseOverviewMember[] }>) =>
    members.length > 0 ? (
        members.map(getWorkMemberName).join(", ")
    ) : (
        <EmptyValue />
    );

const WorkRoleRow = ({
    row,
    field,
    label,
}: Readonly<{ row: WorkTeamRow; field: WorkRoleField; label: string }>) => {
    const labelId = useId();
    const { isRoleEditable } = useWorkEditability();
    return (
        <SettingsPanel.Row label={label} labelId={labelId}>
            {isRoleEditable(row, field) ? (
                <WorkRolePicker
                    row={row}
                    field={field}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="sm"
                            aria-labelledby={labelId}
                        />
                    }
                />
            ) : (
                <WorkRoleNames members={row[field]} />
            )}
        </SettingsPanel.Row>
    );
};

/** The engagement team's roles, a picker each while it has open tasks; each pick saves at once. */
const WorkTeamPanel = ({ row }: Readonly<{ row: WorkTeamRow }>) => (
    <SettingsPanel.Section>
        {WORK_ROLES.map(({ field, label }) => (
            <WorkRoleRow key={field} row={row} field={field} label={label} />
        ))}
    </SettingsPanel.Section>
);

/** `WorkTeamPanel` in List's popover, padded as `SettingsPanel`'s own popup is. */
export const WorkTeamListPanel = ({ row }: Readonly<{ row: WorkTeamRow }>) => (
    <div className={styles.listPanel}>
        <WorkTeamPanel row={row} />
    </div>
);

/** The engagement team popover: a picker per role, opened from `trigger`. */
export const WorkTeamPopover = ({
    row,
    trigger,
}: Readonly<{ row: WorkTeamRow; trigger: ReactElement }>) => (
    <SettingsPanel.Root trigger={trigger}>
        <WorkTeamPanel row={row} />
    </SettingsPanel.Root>
);
